'use client';

import { useEffect, useRef } from 'react';

export default function HeroImageOverlay() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let phase = 0; // 0 = show "syncing", 1 = fade out "syncing", 2 = show synergy animation
    let animationDirection = 1; // 1 = forward, -1 = reverse

    const setCanvasSize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };

    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    let startTime = Date.now();
    const PHASE_DURATION = 3000; // 3 seconds per phase
    let syncingOpacity = 1;
    let synergyProgress = 0;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);

      const centerX = rect.width / 2;
      const centerY = rect.height / 2 - 30; // Move text 30px higher within image frame

      // Calculate responsive sizing based on container dimensions
      const baseSize = Math.min(rect.width, rect.height);
      const scaleFactor = baseSize / 600; // Base reference size
      const fontSize = Math.max(48, Math.min(96, baseSize * 0.16)); // Responsive font size
      const syncingFontSize = fontSize * 0.5; // "syncing" is half the size
      const textSpacing = fontSize * 2.5; // Spacing between AI and HI

      // Calculate phase progression
      if (phase === 0 && elapsed > PHASE_DURATION) {
        phase = 1;
        startTime = Date.now();
      } else if (phase === 1 && elapsed > PHASE_DURATION) {
        phase = 2;
        startTime = Date.now();
      } else if (phase === 2 && elapsed > PHASE_DURATION * 2) {
        phase = 0;
        startTime = Date.now();
        syncingOpacity = 1;
        synergyProgress = 0;
        // Reverse animation direction after each loop
        animationDirection *= -1;
      }

      // Phase 0: Show "syncing" between AI and HI
      if (phase === 0) {
        syncingOpacity = Math.min(1, elapsed / 1000);
        drawText(ctx, 'AI', centerX - textSpacing, centerY, '#00b8ff', 1, fontSize, scaleFactor);
        drawText(ctx, 'syncing', centerX, centerY, '#16c55f', syncingOpacity, syncingFontSize, scaleFactor);
        drawText(ctx, 'HI', centerX + textSpacing, centerY, '#00b8ff', 1, fontSize, scaleFactor);
      }
      // Phase 1: Fade out "syncing"
      else if (phase === 1) {
        syncingOpacity = Math.max(0, 1 - elapsed / 1000);
        drawText(ctx, 'AI', centerX - textSpacing, centerY, '#00b8ff', 1, fontSize, scaleFactor);
        drawText(ctx, 'syncing', centerX, centerY, '#16c55f', syncingOpacity, syncingFontSize, scaleFactor);
        drawText(ctx, 'HI', centerX + textSpacing, centerY, '#00b8ff', 1, fontSize, scaleFactor);

        if (syncingOpacity <= 0) {
          phase = 2;
          startTime = Date.now();
        }
      }
      // Phase 2: Show synergy animation
      else if (phase === 2) {
        let rawProgress = (elapsed % PHASE_DURATION) / PHASE_DURATION;
        // Apply direction to progress
        synergyProgress = animationDirection === 1 ? rawProgress : 1 - rawProgress;
        
        // Draw synergy animation BEFORE text so it flows behind
        drawSynergyAnimation(ctx, centerX - textSpacing, centerY, centerX + textSpacing, centerY, synergyProgress, scaleFactor);
        
        // Draw text on top
        drawText(ctx, 'AI', centerX - textSpacing, centerY, '#00b8ff', 1, fontSize, scaleFactor);
        drawText(ctx, 'HI', centerX + textSpacing, centerY, '#00b8ff', 1, fontSize, scaleFactor);
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', setCanvasSize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  const drawText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    color: string,
    opacity: number,
    fontSize: number,
    scaleFactor: number
  ) => {
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.font = `bold ${fontSize}px "Exo 2", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Scale glow effects based on container size
    const glowScale = Math.max(0.5, Math.min(1.5, scaleFactor));
    
    // Outer glow layer 1 (largest, most diffuse) - Dark blue from logo with 100% opacity
    ctx.shadowColor = '#031b3a';
    ctx.shadowBlur = 60 * glowScale;
    ctx.fillStyle = '#031b3a';
    ctx.fillText(text, x, y);
    
    // Outer glow layer 2 (medium) - Dark blue from logo with 100% opacity
    ctx.shadowBlur = 40 * glowScale;
    ctx.fillText(text, x, y);
    
    // Outer glow layer 3 (tighter) - Dark blue from logo with 100% opacity
    ctx.shadowBlur = 25 * glowScale;
    ctx.fillText(text, x, y);
    
    // Inner glow for depth - Dark blue from logo with 100% opacity
    ctx.shadowBlur = 15 * glowScale;
    ctx.fillText(text, x, y);
    
    // Core text with slight glow - Dark blue from logo with 100% opacity
    ctx.shadowBlur = 8 * glowScale;
    ctx.fillStyle = '#ffffff';
    ctx.fillText(text, x, y);
    
    // Final sharp text layer
    ctx.shadowBlur = 0;
    ctx.fillStyle = color;
    ctx.fillText(text, x, y);
    
    ctx.restore();
  };

  const drawSynergyAnimation = (
    ctx: CanvasRenderingContext2D,
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    progress: number,
    scaleFactor: number
  ) => {
    ctx.save();

    // Scale particle sizes based on container
    const particleScale = Math.max(0.5, Math.min(1.5, scaleFactor));

    // Draw connecting energy particles
    const numParticles = 8;
    for (let i = 0; i < numParticles; i++) {
      const offset = (i / numParticles + progress) % 1;
      const x = startX + (endX - startX) * offset;
      const y = startY + (endY - startY) * offset;
      const size = (4 + Math.sin(offset * Math.PI) * 3) * particleScale;
      
      // Particle glow
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, size * 2);
      gradient.addColorStop(0, 'rgba(22, 197, 95, 1)');
      gradient.addColorStop(0.5, 'rgba(22, 197, 95, 0.6)');
      gradient.addColorStop(1, 'rgba(22, 197, 95, 0)');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, size * 2, 0, Math.PI * 2);
      ctx.fill();
      
      // Core particle
      ctx.fillStyle = '#16c55f';
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw connecting line with gradient
    const lineGradient = ctx.createLinearGradient(startX, startY, endX, endY);
    lineGradient.addColorStop(0, '#00b8ff');
    lineGradient.addColorStop(0.5, '#16c55f');
    lineGradient.addColorStop(1, '#00b8ff');
    
    ctx.strokeStyle = lineGradient;
    ctx.lineWidth = 2 * particleScale;
    ctx.shadowColor = '#16c55f';
    ctx.shadowBlur = 15 * particleScale;
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();

    // Draw pulsing connection symbol in center
    const centerX = (startX + endX) / 2;
    const centerY = (startY + endY) / 2;
    const pulseSize = (40 + Math.sin(progress * Math.PI * 4) * 16) * particleScale;
    
    // Outer glow
    const symbolGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, pulseSize * 1.5);
    symbolGradient.addColorStop(0, 'rgba(0, 184, 255, 0.8)');
    symbolGradient.addColorStop(0.5, 'rgba(22, 197, 95, 0.4)');
    symbolGradient.addColorStop(1, 'rgba(22, 197, 95, 0)');
    
    ctx.fillStyle = symbolGradient;
    ctx.beginPath();
    ctx.arc(centerX, centerY, pulseSize * 1.5, 0, Math.PI * 2);
    ctx.fill();
    
    // Inner symbol (rotating)
    const rotation = progress * Math.PI * 2;
    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(rotation);
    
    // Draw connection symbol (two interlocking circles)
    ctx.strokeStyle = '#00b8ff';
    ctx.lineWidth = 3 * particleScale;
    ctx.shadowColor = '#00b8ff';
    ctx.shadowBlur = 10 * particleScale;
    
    ctx.beginPath();
    ctx.arc(-pulseSize / 4, 0, pulseSize / 2, 0, Math.PI * 2);
    ctx.stroke();
    
    ctx.beginPath();
    ctx.arc(pulseSize / 4, 0, pulseSize / 2, 0, Math.PI * 2);
    ctx.stroke();
    
    ctx.restore();
    ctx.restore();
  };

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 10 }}
    />
  );
}